import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ScheduleTemplate } from '@/types/schedule.types';

export interface ScheduleTemplatesParams {
  page?: number;
  limit?: number;
  search?: string;
  source?: 'all' | '1c' | 'local';
}

export interface ScheduleTemplatesResponse {
  items: ScheduleTemplate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const useScheduleTemplates = (params: ScheduleTemplatesParams = {}) => {
  return useQuery<ScheduleTemplatesResponse>({
    queryKey: ['scheduleTemplates', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/schedule-templates', {
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          search: params.search || undefined,
          source: params.source || undefined,
        },
      });
      // api.get() возвращает { data: [...], total, page, limit } напрямую
      // НЕ обёрнутый в response.data (как в axios)
      return {
        items: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: Math.ceil((response.total || 0) / (response.limit || 20)),
      };
    },
  });
};

export const useScheduleTemplate = (id: string | null) => {
  return useQuery<ScheduleTemplate>({
    queryKey: ['scheduleTemplate', id],
    queryFn: async () => {
      if (!id) throw new Error('Template ID is required');
      const response = await api.get(`/api/v1/schedule-templates/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateScheduleTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<ScheduleTemplate>) => {
      // Convert days_pattern object to JSON string for backend
      const payload = {
        ...data,
        days_pattern: typeof data.days_pattern === 'object'
          ? JSON.stringify(data.days_pattern)
          : data.days_pattern,
      };
      const response = await api.post('/api/v1/schedule-templates', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplates'] });
    },
  });
};

export const useUpdateScheduleTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ScheduleTemplate> }) => {
      try {
        // Convert days_pattern object to JSON string for backend (same as create)
        const payload = {
          ...data,
          days_pattern: typeof data.days_pattern === 'object'
            ? JSON.stringify(data.days_pattern)
            : data.days_pattern,
        };
        const response = await api.patch(`/api/v1/schedule-templates/${id}`, payload);
        return response.data;
      } catch (error: any) {
        // Handle 403 Forbidden error for 1C templates
        if (error?.response?.status === 403) {
          const message = error?.response?.data?.message || 
            'Этот шаблон из 1С. Редактирование возможно только в системе 1С.';
          throw new Error(message);
        }
        // Re-throw other errors
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplate', variables.id] });
      // 🔄 IMPORTANT: Invalidate calendar cache so changes appear immediately
      queryClient.invalidateQueries({ queryKey: ['templateCalendar', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['templateCalendarRange', variables.id] });
    },
  });
};

export const useDeleteScheduleTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/schedule-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplates'] });
    },
  });
};

export interface AssignTemplateParams {
  templateId: string;
  employeeIds: string[];
  validFrom: string;      // Обязательно: YYYY-MM-DD
  validUntil?: string;    // Опционально: YYYY-MM-DD
}

export interface AssignTemplateResponse {
  message: string;
  assignments: Array<{
    id: string;
    tenant_id: string;
    employee_id: string;
    template_id: string;
    valid_from: string;
    valid_until: string | null;
    created_at: string;
  }>;
}

export const useAssignScheduleTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation<AssignTemplateResponse, Error, AssignTemplateParams>({
    mutationFn: async ({ templateId, employeeIds, validFrom, validUntil }) => {
      const response = await api.post(`/api/v1/schedule-templates/${templateId}/assign`, {
        employee_ids: employeeIds,
        valid_from: validFrom,
        valid_until: validUntil || undefined,
      });
      return response.data || response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['templateAssignments', variables.templateId] });
    },
  });
};

// Interface for template assignment (employee assigned to template)
export interface TemplateAssignment {
  id: string;
  employee_id: string;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    middle_name: string | null;
    is_active: boolean;
    position: {
      id: string;
      name: string;
    } | null;
    department: {
      id: string;
      name: string;
    } | null;
  } | null;
  valid_from: string;
  valid_until: string | null;
  created_at: string;
}

export interface TemplateAssignmentsResponse {
  template_id: string;
  template_name: string;
  total: number;
  assignments: TemplateAssignment[];
}

export const useTemplateAssignments = (templateId: string | null) => {
  return useQuery<TemplateAssignmentsResponse>({
    queryKey: ['templateAssignments', templateId],
    queryFn: async () => {
      if (!templateId) throw new Error('Template ID is required');
      const response = await api.get(`/api/v1/schedule-templates/${templateId}/assignments`);
      return response.data || response;
    },
    enabled: !!templateId,
  });
};

// Update work time for template (allowed for 1C templates)
export interface UpdateWorkTimeParams {
  templateId: string;
  shiftStart: string;  // HH:MM format
  shiftEnd: string;    // HH:MM format
}

export interface UpdateWorkTimeResponse {
  message: string;
  template_id: string;
  template_name: string;
  shift_start: string;
  shift_end: string;
  updated_days: number;
  is_night_shift: boolean;
}

export const useUpdateWorkTime = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateWorkTimeResponse, Error, UpdateWorkTimeParams>({
    mutationFn: async ({ templateId, shiftStart, shiftEnd }) => {
      const response = await api.patch(`/api/v1/schedule-templates/${templateId}/work-time`, {
        shift_start: shiftStart,
        shift_end: shiftEnd,
      });
      return response.data || response;
    },
    onSuccess: (_, variables) => {
      // Invalidate template and calendar caches
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['scheduleTemplate', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['templateCalendar', variables.templateId] });
      queryClient.invalidateQueries({ queryKey: ['templateCalendarRange', variables.templateId] });
    },
  });
};